import { ACTIVITY_MAX_SCHEMA_KEY_LENGTH, ACTIVITY_SANITIZED_FIELD_KEY_PREFIX, ACTIVITY_SCHEMA_KEY_PATTERN } from 'utils/activityTracking/constants';

/**
 * Guards the `field_key` / `target_id` schema key for the activity event stream (BEAST-13036).
 *
 * The key is `formConfig.fields[].dataField`, a schema key by construction - every observed value
 * (`cargo.sealNumber`, `desk.name`, `startedUtcDateTime`) already satisfies the shared schema's
 * pattern. But the form config is server-driven, so the full set cannot be proven statically, and
 * one key with a space or an `items[0].name` style path would fail the shared schema and destroy
 * every batch it happened to land in (the shared service has no partial acceptance).
 *
 * So a key that misses the pattern or the length limit is neither sent raw nor dropped: it is
 * rewritten through this one documented transform, and the original is logged once per key per
 * session so we learn which form configs need fixing. A config we have never seen degrades one
 * field's label instead of destroying batches.
 */

/** Keys already reported this session, so an unknown config logs once and not once per focus. */
const reportedKeys = new Set();

/**
 * djb2, as a stable non-cryptographic fallback identity for a key we cannot send as-is. It only has
 * to be deterministic across reloads and collision-resistant enough to tell two bad keys apart.
 */
function stableHash(value) {
    let hash = 5381;
    for (let i = 0; i < value.length; i += 1) {
        hash = ((hash << 5) + hash + value.charCodeAt(i)) >>> 0;
    }
    return hash.toString(16);
}

/**
 * @param {string} rawKey the `dataField` straight off the form config
 * @returns {string|null} a key guaranteed to satisfy the shared schema, or `null` when there is no
 *   usable key at all (an empty or non-string input), in which case the caller emits no event.
 */
export function toSchemaFieldKey(rawKey) {
    if (typeof rawKey !== 'string') return null;

    const trimmed = rawKey.trim();
    if (!trimmed) return null;

    if (ACTIVITY_SCHEMA_KEY_PATTERN.test(trimmed) && trimmed.length <= ACTIVITY_MAX_SCHEMA_KEY_LENGTH) {
        return trimmed;
    }

    // Strip everything the pattern disallows. A path-style key such as `items[0].name` survives as
    // `items0.name`, which is still readable and still maps back to the original in the logs.
    const stripped = trimmed.replace(/[^a-zA-Z0-9_.:-]/g, '');
    const sanitized =
        stripped && stripped.length <= ACTIVITY_MAX_SCHEMA_KEY_LENGTH ? stripped : `${ACTIVITY_SANITIZED_FIELD_KEY_PREFIX}${stableHash(trimmed)}`;

    if (!reportedKeys.has(trimmed)) {
        reportedKeys.add(trimmed);
        console.warn(
            `activityTracking: field key "${trimmed}" does not satisfy the activity event schema and was sent as "${sanitized}". ` +
                'The form config should be corrected so the key needs no transform.'
        );
    }

    return sanitized;
}

/** Test seam only: the report-once set is module state that would otherwise leak between tests. */
export function resetReportedFieldKeys() {
    reportedKeys.clear();
}
